/**
 * @file        job/result-handle.js
 * @author      Ryan Rossiter, ryan@kingsds.network
 *              Robert Mirandola, <robert@kingsds.network>
 * @date        June 2020
 *              July 2022
 *
 * The ResultHandle acts as a proxy for a job's results, querying
 * internal results when available or the scheduler when the results
 * are not available locally.
 */

const { rehydrateRange, RangeObject, SparseRangeObject } = require('../range-object');
const protocolV4 = require('dcp/protocol');
const wallet = require('dcp/dcp-client/wallet');
const { fetchResults } = require('./download-slices.js');
const { DCPError } = require('dcp/common/dcp-error');
const customInspectSymbol = Symbol.for('nodejs.util.inspect.custom');
const debugging = require('dcp/debugging').scope('result-handle');

// Array methods that don't modify the array, will be applied to the ResultHandle's values object
const RH_ARRAY_METHODS = [
  'slice', 'filter', 'concat', 'find', 'findIndex', 'indexOf', 'map', 'reduce', 'includes', 'toString', 'forEach'
];

/**
 * Heuristic to detect whether 'n' came from a numeric literal that got converted to a string?
 * For instance, in `a[5]`, `5` would return 5. And in `a['05']`, `'05'` should return undefined.
 * Note that `a[0005]` and `a['5']` are indistinguishable, but it doesn't matter for practical purposes.
 *
 * @param {string | symbol} n
 * @returns {number | undefined} the index if it's likely an index, or undefined otherwise
 */
function indexIfValid(n)
{
  if (typeof n !== 'string')
    return undefined;

  const parsed = parseFloat(n);
  return Number.isInteger(parsed) && n === String(parsed)
    ? parsed
    : undefined;
}

/**
 * This class represents a handle on a job's results. It can be used for accessing the job's results, or for querying the scheduler to fetch results.
 * In addition to the properties and methods of this class, the following standard array methods are also available for accessing the available results: `slice`, `filter`, `concat`, `find`, `findIndex`, `indexOf`, `map`, `reduce`, `includes`, `toString`, and `forEach`.
 * The results can also be accessed by index (`results[i]`), but an error will be thrown if the result for that index is not yet available.
 * @access public
 */
class ResultHandle extends Array
{
  // even in a proxy, ownKeys trap must return all non-configurable own properties of the target object
  // see https://tc39.es/ecma262/#sec-proxy-object-internal-methods-and-internal-slots-ownpropertykeys
  // hence we use private fields, since they are truly invisble to all but us
  #job;
  #realKeys;
  #realValues;
  #valuesAvailable;

  constructor(job)
  {
    super();

    const { Job } = require('.');
    if (!(job instanceof Job))
      throw new TypeError('ResultHandle must be constructed from a Job object');

    this.#job = job;
    this.#realKeys = job.jobInputData;
    this.#realValues = [];
    this.#valuesAvailable = [];

    return new Proxy(this, {
      get(target, name)
      {
        const i = indexIfValid(name);

        if (i !== undefined)
        {
          if (target.isAvailable(i))
            return target.#realValues[i];
          else
            throw new Error(`Result ${i} is not available. It has either not been computed or you need to fetch it.`);
        } 
        else if (name === 'length')
          return target.getLength();
        else if (name === 'constructor')
          return Array.constructor;
        else if (typeof name === 'string' && RH_ARRAY_METHODS.includes(name))
        {
          let values = target.values();
          return values[name].bind(values);
        }
        else
        {
          // only return methods on this class, don't allow access
          // to array methods other than the allowlisted ones
          const p = Object.getPrototypeOf(target)[name];
          if (typeof p === 'function') {
            return p.bind(target);
          } else {
            return p;
          }
        }
      },

      getOwnPropertyDescriptor(target, prop)
      {
        return Reflect.getOwnPropertyDescriptor(target.values(), prop);
      },


      has(target, prop)
      {
        const i = indexIfValid(prop)
        if (i !== undefined)
          return target.isAvailable(i);

        const available = target.values();

        if (Reflect.ownKeys(available).includes(prop))
          return true;

        // only allow searching symbol or the allowed methods on the prototype
        return RH_ARRAY_METHODS.includes(String(prop)) || typeof prop === 'symbol'
          ? Reflect.has(target, prop)
          : false;
      },

      // rationale for disallowing modification or assignment to result handle is that
      // we disallow methods that mutate the array anyway. Allowing ad hoc mutations
      // makes invariants harder to keep and inconsistent with the allowed methods.
      defineProperty(_target, _prop, _attr)
      {
        throw new TypeError('Mutating the ResultHandle is not allowed, consider making a shallow copy via Array.from');
      },

      set(_target, _prop, _val)
      {
        throw new TypeError('Mutating the ResultHandle is not allowed, consider making a shallow copy via Array.from');
      },

      deleteProperty(_target, _prop)
      {
        throw new TypeError('Mutating the ResultHandle is not allowed, consider making a shallow copy via Array.from');
      },

      ownKeys(target)
      {
        return Reflect.ownKeys(target.values())
      },
    });
  }

  isAvailable(index)
  {
    return this.#valuesAvailable[index];
  }

  reset() {
    // quickly empty the #realValues array
    this.#realValues.length = this.#valuesAvailable.length = 0;
    this.#realValues.length = this.#valuesAvailable.length =this.#realKeys.length;
  }

  /**
   * Returns an array of input values. Will only return input values that have a completed result available.
   * @access public
   * @returns {Array<*>}
   */
  keys() {
    // Keys can be a RangeObject, faster to iterate over #valuesAvailable
    return this.#valuesAvailable.reduce((keysList, valueAvailable, index) => {
      if (valueAvailable) keysList.push(this.#realKeys[index]);
      return keysList;
    }, []);
  }

  /**
   * Returns an array of results. Will only return results that have been received from the scheduler, if only one result is complete the array will contain one value.
   * @access public
   * @returns {Array<*>}
   */
  values() {
    return this.#realValues.filter((v, i) => this.isAvailable(i));
  }

  // Aliased as length in proxy, can't use getter because Array.length is not configurable
  getLength() {
    return this.#valuesAvailable.reduce((n, v) => (n + (v ? 1 : 0)), 0);
  }
    

  [customInspectSymbol]() /* overrides the output when console.log(result) is called in Node*/
  { 
    return this.values(); 
  }

  [Symbol.iterator]() {
    let index = 0;
    let values = this.values(); // use available values

    return {
      next: () => ({
        value: values[index++],
        done: index > values.length
      })
    };
  }

  /** 
   * Returns an array of [input, output] pairs, in sliceNumber order.
   * Return value is undefined if the input is not an ES5 primitive.
   * @access public
   * @returns {Array<*>}
   */
  entries() {
    return this.#valuesAvailable.reduce((keyValuePairs, valueAvailable, index) => {
      if (valueAvailable) keyValuePairs.push([
        String(this.#realKeys[index]),
        this.#realValues[index],
      ]);
      return keyValuePairs;
    }, []);
  }

  /** 
   * Returns an Object associating input and output values where the inputs are ES5 primitive types.
   * Return value is undefined if the input is not an ES5 primitive.
   * @access public
   * @returns {object}
   */
  fromEntries() {
    return this.entries().reduce((o, [k, v]) => {
      o[k] = v; return o;
    }, {});
  }

  /** 
   * Return the nth input value/input vector 
   * @access public
   * @param {number} n Index in the input set to return the value for.
   * @returns {*} Input set value
   */
  key(n) {
    return this.#realKeys[n];
  }

  /** 
   * Return the value corresponding to the provided key 
   * @access public
   * @param {*} key Corresponds to a value in the job's input set.
   * @returns {*} Result corresponding to the input value
   */
  lookupValue(key) {
    // use keys instead of _keys so we only lookup on available results
    let ind = this.keys().indexOf(key);
    if (ind === -1)
      throw new DCPError('Argument passed into the function was not found in input set', 'ERANGE');

    return this.values()[ind];
  }

  /**
   * Sends request to scheduler to fetch results, the retrieved results will be populated on this object.
   * @param {RangeObject} [rangeObject] - range object to query results
   * @param {string} [emitEvents] - if truthy, emits a `result` event for new results as they are added to the handle, and if set to 'all' emits 'resultUpdated' for duplicate results 
   * @access public
   * @emits Job#resultsUpdated
   */
  async fetch(rangeObject, emitEvents) {
    // Fetch any existing results
    const results = await fetchResults(this.#job.id, rangeObject);
    
    for (const r of results)
    {
      this.#realValues[r.slice - 1] = r.value;
      if (emitEvents)
      {
        if (!this.#valuesAvailable[r.slice - 1])
          this.#job.emit('result', { sliceNumber: r.slice, result: this.#realValues[r.slice] });
        else if (emitEvents === 'all')
        {
          this.#job.emit('resultsUpdated', { sliceNumber: r.slice });
          this.#job.emit('result', { sliceNumber: r.slice, result: this.#realValues[r.slice] });
        }
      }
      this.#valuesAvailable[r.slice - 1] = true;
    };
  }
  
  /**
   * Handle adding a new result
   */
  newResult(result, index)
  {
    if (this.#valuesAvailable[index])
    {
      this.#realValues[index] = result;
      this.#job.emit('resultsUpdated', { sliceNumber: index + 1 });
    }
    else
    {
      this.#valuesAvailable[index] = true;
      this.#realValues[index] = result;
      this.#job.emit('result', { job: this.#job.id, sliceNumber: index + 1, result });
    }
  }
  
  list(rangeObject) {
    let range = rangeObject && rehydrateRange(rangeObject);
    if (!range) range = new RangeObject(1, this.#valuesAvailable.length);
    let sparseObjects = [];
    let min = range.start + range.step;
    let goodRange = false;
    let step = range.step;
    for (let i = range.start; i <= range.end; i+= step)
    {
      if (!goodRange && this.isAvailable(i - 1))
      {
        min = i
        goodRange = true;
      }
      if (goodRange && !this.isAvailable(i - 1))
      {
        if (min !== i-step)
          sparseObjects.push(new RangeObject(min, i-step, step))
        else
          sparseObjects.push(new RangeObject(min, i-step))
        goodRange = false;
      }
    }
    if (goodRange)
    {
      if (min !== range.end)
        sparseObjects.push(new RangeObject(min, range.end, step))
      else
        sparseObjects.push(new RangeObject(min, range.end))
    }

    try
    {
      return new SparseRangeObject(...sparseObjects);
    }
    catch
    {
      return [];
    }

  }

  async stat(rangeObject) {
    const range = rehydrateRange(rangeObject);
    throw new Error("ResultHandle.stat not implemented");
  }
}

Object.assign(exports, {
  ResultHandle,
});
